import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import {
  GraphQLAPI,
  GraphQLResult,
  parseClassroom,
  queries,
} from "@examspark/api";
import {
  collectAllPages,
  delayForIndexing,
  getTagFromResource,
} from "@examspark/common";
import {
  Classroom,
  Course,
  IDSK,
  ParsedClassroom,
  Resource,
  SearchableResourceConnectionNew,
  Unit,
  UpdateResourceInput,
  SearchableResourceFilterInput,
  isClassroom,
  isCourse,
  isUnit,
} from "@examspark/types";

import { useAuthorizationContext } from "../../hooks/useAuthorizationContext";

import { useQueryGetCourseUnits } from "./course";

export type ResourceLocation =
  | Course
  | Unit
  | (Classroom & { resourceUnit?: Unit });

export type DecomposedResourceLocation = {
  course: Course | undefined;
  unit: Unit | undefined;
  classroom: ParsedClassroom | undefined;
};

export type ResourceWithLocations = {
  resource: Resource;
  locations: ResourceLocation[];
};

type LocationType =
  | "Course"
  | "Unit"
  | "Classroom"
  | "ClassroomUnit"
  | undefined;

export const useQuerySearchResourcesForLocation = (
  location: ResourceLocation | undefined,
) => {
  const authorizationContext = useAuthorizationContext();

  const { course, unit, classroom, locationType } =
    decomposeResourceLocation(location);

  const courseUnits = useQueryGetCourseUnits(course?.id);

  return useQuery({
    queryKey: ["resources", locationType, course?.id, unit?.id, classroom?.id],
    enabled: location?.id != null && !courseUnits.isLoading,
    queryFn: async () => {
      const locationFilter = getResourceLocationFilter(
        { course, unit, classroom },
        courseUnits.data,
      );
      if (locationFilter == null) {
        if (location != null) {
          console.error("Failed to get location filter for location", location);
        }
        return [];
      }

      const all = await collectAllPages(async (nextToken) => {
        const result = (await GraphQLAPI.query<{
          search: SearchableResourceConnectionNew;
        }>({
          query: queries.search.search({
            label: `Search${locationType}Resources`,
            fields: `
                id
                sk
                workspaceId
                name
                type
                visibility
                file {
                  bucket
                  key
                }
                resource {
                  id
                  sk
                  name
                }
                tags {
                  key
                  value
                }
                workspace {
                  id
                  name
                }
              `,
          }),
          variables: {
            filter: {
              sk: {
                eq: "resource#v0",
              },
              or: locationFilter,
            },
            sort: [
              {
                field: "name",
                direction: "asc",
              },
            ],
            authorizationContext,
            nextToken,
          },
        })) as GraphQLResult<{ search: SearchableResourceConnectionNew }>;

        return result.data?.search;
      });

      return all
        .flatMap((a) => a.items ?? [])
        .map((item) => {
          return {
            resource: item,
            locations: determineResourceLocations(
              item,
              { course, unit, classroom },
              courseUnits.data,
            ),
          } as ResourceWithLocations;
        });
    },
  });
};

export const decomposeResourceLocation = (
  location: ResourceLocation | undefined,
): DecomposedResourceLocation & { locationType: LocationType } => {
  let course: Course | undefined,
    unit: Unit | undefined,
    classroom: Classroom | undefined;

  let locationType: LocationType | undefined;

  if (isClassroom(location)) {
    course = location.course ?? undefined;
    locationType = "Classroom";
    if (location.resourceUnit != null) {
      unit = location.resourceUnit ?? undefined;
      locationType = "ClassroomUnit";
    }
    classroom = location;
  } else if (isUnit(location)) {
    course = location.course ?? undefined;
    unit = location;
    locationType = "Unit";
  } else if (isCourse(location)) {
    course = location;
    locationType = "Course";
  }

  return { course, unit, classroom: parseClassroom(classroom), locationType };
};

const getResourceLocationFilter = (
  { course, unit, classroom }: DecomposedResourceLocation,
  courseUnits: Unit[] | undefined,
) => {
  const filter: SearchableResourceFilterInput[] = [];

  if (course?.id != null) {
    filter.push({
      referencesIds: { eq: `course#${course.id}` },
    });
    if (unit?.id != null) {
      filter.push({ referencesIds: { eq: `unit#${unit.id}` } });
      filter.push({
        and: [
          { tags: { eq: `course-id#${course.id}` } },
          { tags: { eq: `unit-id#${unit.id}` } },
        ],
      });
      filter.push({
        and: [
          { tags: { eq: `course-id#${course.id}` } },
          { tags: { ne: "unit-id" } },
        ],
      });
    } else {
      filter.push(
        ...(courseUnits?.map((u) => ({
          referencesIds: { eq: `unit#${u.id}` },
        })) ?? []),
      );
      filter.push({ tags: { eq: `course-id#${course.id}` } });
    }
  }

  if (classroom?.id != null) {
    if (unit?.id != null) {
      filter.push({
        and: [
          { tags: { eq: `classroom-id#${classroom?.id}` } },
          { tags: { eq: `unit-id#${unit?.id}` } },
        ],
      });
      filter.push({
        and: [
          { tags: { eq: `classroom-id#${classroom?.id}` } },
          { tags: { ne: `unit-id` } },
        ],
      });
    } else {
      filter.push({ tags: { eq: `classroom-id#${classroom?.id}` } });
    }
  }

  if (filter.length === 0) return undefined;

  if (classroom?.id == null) {
    return [
      {
        and: [{ or: filter }, { tags: { ne: "classroom-id" } }],
      },
    ];
  }

  return filter;
};

const determineResourceLocations = (
  item: Resource,
  { course, classroom }: DecomposedResourceLocation,
  courseUnits: Unit[] | undefined,
): ResourceLocation[] => {
  const locations: ResourceLocation[] = [];

  const taggedCourseId = getTagFromResource(item, "course-id")?.value;
  const taggedUnitId = getTagFromResource(item, "unit-id")?.value;
  const taggedClassroomId = getTagFromResource(item, "classroom-id")?.value;

  if (course?.resourcesIds?.find((r) => r.id === item.id)) {
    locations.push(course);
  } else if (
    course != null &&
    taggedCourseId === course?.id &&
    taggedUnitId == null &&
    taggedClassroomId == null
  ) {
    locations.push(course);
  }

  for (const courseUnit of courseUnits ?? []) {
    if (courseUnit.resourcesIds?.find((r) => r.id === item.id)) {
      locations.push(courseUnit);
    } else if (taggedUnitId === courseUnit.id && taggedClassroomId == null) {
      locations.push(courseUnit);
    }
  }

  if (classroom != null && taggedClassroomId === classroom?.id) {
    if (taggedUnitId == null) {
      locations.push({
        ...classroom,
        resourceUnit: undefined,
      });
    } else {
      const classroomUnit = classroom.units?.find(
        (u) => u.unit?.id === taggedUnitId,
      );
      if (classroomUnit != null) {
        locations.push({
          ...classroom,
          resourceUnit: classroomUnit.unit ?? undefined,
        });
      }
    }
  }

  return locations;
};

export const useQueryGetResource = (key: Partial<IDSK>) => {
  return useQuery(getResource(key));
};

export const useQueriesGetResources = (keys: Array<IDSK> | undefined) => {
  return useQueries({
    queries: keys?.map(getResource) ?? [],
  });
};

export const getResource = (key: Partial<IDSK>) => ({
  queryKey: ["resource", key.id],
  enabled: !!key.id,
  queryFn: async () => {
    const result = (
      (await GraphQLAPI.query({
        query: queries.resource.getResource({
          fields: `
            id
            sk
            name
            type
            content {
              body
            }
          `,
        }),
        variables: key,
      })) as any
    ).data.getResource;

    return result as Resource;
  },
});

export const useQueryGetDocument = (key: Partial<IDSK>) => {
  return useQuery<Resource>({
    queryKey: ["document", key.id],
    enabled: !!key.id,
    queryFn: async () => {
      const result = (
        (await GraphQLAPI.query({
          query: queries.resource.getResource({
            fields: `
              id
              sk
              workspaceId
              name
              type
              file {
                bucket
                name
                mimetype
                key
                size
              }
            `,
          }),
          variables: key,
        })) as any
      ).data.getResource;

      return result;
    },
  });
};

export const useMutationUpdateResource = () => {
  const authorizationContext = useAuthorizationContext();

  const client = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdateResourceInput) => {
      const result = await GraphQLAPI.query<{
        updateResource: Resource;
      }>({
        query: queries.resource.updateResource(),
        variables: {
          input,
          authorizationContext,
        },
      });

      //
      // Wait for Elasticsearch
      //
      await delayForIndexing();

      client.invalidateQueries(["resources"]);
      client.invalidateQueries({
        predicate: (query) => {
          return query.queryKey.includes(input.id);
        },
      });
      if (input.sk === "exam#v0") {
        client.invalidateQueries([
          "playable-exam",
          { id: input.id, sk: input.sk },
        ]);
      }

      return result;
    },
  });
};

export const useMutationDeleteResource = () => {
  const client = useQueryClient();
  const authorizationContext = useAuthorizationContext();

  return useMutation({
    mutationFn: async (input: IDSK) => {
      const result = (await GraphQLAPI.query<{
        deleteResource: Resource;
      }>({
        query: queries.resource.deleteResource(),
        variables: {
          input: {
            id: input.id,
            sk: input.sk,
            workspaceId: authorizationContext.workspaceId,
          },
        },
      })) as GraphQLResult<{
        deleteResource: Resource;
      }>;

      await delayForIndexing();

      client.invalidateQueries(["resources"]);
      client.invalidateQueries({
        predicate: (query) => query.queryKey.includes(input.id),
      });

      return result;
    },
  });
};
